/**
 * Track events from the store. These are fired up to the sdk layer to whatever tracking mechanism is implemented.
 */
import type { Middleware } from 'redux';
import { RootState } from '../../store';
import * as Actions from '../actions/app';
import * as Logging from '../actions/log';
import * as Selectors from '../selectors';

export let trackEvent: (event: string, properties?: { [key: string]: any }) => void = () => {};
export const initialiseTracking = (
    trackEventHandler: (event: string, properties?: { [key: string]: any }) => void,
) => {
    trackEvent = trackEventHandler;
};

export const middleware: Middleware =
    ({ dispatch, getState }) =>
    next =>
    action => {
        const appState: RootState = getState();

        const trackWithLogging = (event: string, properties?: { [key: string]: any }) => {
            // Track current items and app version on every event
            const items: Array<string> = [];
            appState.basket.basket?.products.forEach(p => {
                for (let i = 0; i < p.quantity; i++) {
                    items.push(p.productId);
                }
            });
            const basketValue = Selectors.Basket.price(appState);
            trackEvent(event, { ...properties, items, value: basketValue });
            dispatch(Logging.logDebug('Track Event: ' + event, properties));
        };

        switch (action.type) {
            case Actions.loadingFinished:
                trackWithLogging('loading_finished');
                break;
            case Actions.user3dSceneInteraction:
                trackWithLogging('scene_interaction');
                break;
            case Actions.dimensionsToggled:
                trackWithLogging('toggle_dimensions', {
                    toggle_action: action.payload.isVisible ? 'hide' : 'show',
                });
                break;
            case Actions.configureButtonVisible:
                trackWithLogging('configure_button_visible', { visible: action.payload });
                break;
            case Actions.shareModalOpened:
                trackWithLogging('share_modal_opened');
                break;
            case Actions.shareButtonClicked:
                trackWithLogging('share_button_clicked', { target: action.payload });
                break;
            case Actions.shareLinkCopied:
                trackWithLogging('share_link_copied');
                break;
            case Actions.configurePanelToggle:
                const open = action.payload;
                if (open) {
                    trackWithLogging('configure_panel_open');
                } else {
                    trackWithLogging('configure_panel_close');
                }
                break;
            case Actions.viewInAr:
                trackWithLogging('view_in_ar');
                break;
            case Actions.openArDrawer:
                trackWithLogging('open_ar_drawer');
                break;
            case Actions.closeArDrawer:
                trackWithLogging('close_ar_drawer');
                break;
            case Actions.selectPartToConfigure:
                const part = action.payload.part;
                if (part !== null) {
                    if (part === 'adjust') {
                        trackWithLogging('select_part_to_configure', { part: 'default' });
                    } else {
                        trackWithLogging('select_part_to_configure', { part });
                    }
                }
                break;
            case Actions.selectProductCard:
                const new_item_id = action.payload.newItemId;
                const compatible = action.payload.compatible;
                const price = action.payload.price;
                if (new_item_id) {
                    trackWithLogging('select_product_card', {
                        new_item_id,
                        compatible,
                        price,
                    });
                }
                break;
            case Actions.undo:
                trackWithLogging('undo_redo', { type: 'undo' });
                break;
            case Actions.redo:
                trackWithLogging('undo_redo', { type: 'redo' });
                break;
            case Actions.addParts:
                trackWithLogging('add_parts');
                break;
            case Actions.removeParts:
                trackWithLogging('remove_parts');
                break;
            case Actions.addPartsLoadingDuration:
                trackWithLogging('add_parts_loading_duration', { duration: action.payload });
                break;
            case Actions.changeSelectionsLoadingDuration:
                trackWithLogging('change_selections_loading_duration', {
                    measure: action.payload.measure,
                    solutionMode: action.payload.solutionMode,
                    selectionType: action.payload.selectionType,
                    isCompatible: action.payload.isCompatible,
                });
                break;
            case Actions.addPartsButtonVisible:
                trackWithLogging('add_parts_button_visible', { isVisible: action.payload });
                break;
            case Actions.moreInfoClicked:
                trackWithLogging('product_card_more_info', { price: action.payload });
                break;
            case Actions.variantQuestionView:
                trackWithLogging('variant_question_view', { category: action.payload });
                break;
            case Actions.variantSelectionApplied:
                trackWithLogging('variant_selection_applied', { variant: action.payload });
                break;
            case Actions.variantColourSelectionApplied:
                trackWithLogging('variant_colour_selection_applied', { colour: action.payload });
                break;
            case Actions.compatibilityToggled:
                trackWithLogging('toggle_compatibility', { compatibility: action.payload });
                break;
            case Actions.closeFromError:
                trackWithLogging('close_application_button');
                break;
            case Actions.globalError:
                trackWithLogging('global_error', { error: action.payload });
                break;
            case Actions.selectPiece:
                trackWithLogging('select_piece', {
                    selectedPiecePosition: action.payload.selectedPiecePosition,
                });
                break;
            case Actions.changePiece:
                trackWithLogging('change_piece', {
                    newPiece: action.payload.newPiece,
                    newPiecePosition: action.payload.newPiecePosition,
                });
                break;
            case Actions.addButtonClick:
                trackWithLogging('add_button_click', {
                    selectedPiecePosition: action.payload.selectedPiecePosition,
                    buttonSide: action.payload.buttonSide,
                });
                break;
            case Actions.addPiece:
                trackWithLogging('add_piece', {
                    addedPiece: action.payload.addedPiece,
                    addedPiecePosition: action.payload.addedPiecePosition,
                });
                break;
            case Actions.removePiece:
                trackWithLogging('remove_piece', {
                    removedPiecePosition: action.payload.removedPiecePosition,
                });
                break;
            case Actions.openSelectionsMenu:
                trackWithLogging('open_global_selections_menu');
                break;
            case Actions.changeSelection:
                trackWithLogging('change_global_selections', {
                    newSelections: action.payload.newSelections,
                });
                break;
            case Actions.viewBasket:
                trackWithLogging('view_basket');
                break;
            case Actions.viewInRoom:
                trackWithLogging('view_in_room');
                break;
            case Actions.viewCatalogueTab:
                trackWithLogging('view_catalogue_tab', {
                    catalogueTab: action.payload.catalogueTab,
                });
                break;
            case Actions.viewCataloguePanel:
                trackWithLogging('view_catalogue_panel', {
                    catalogueTab: action.payload.catalogueTab,
                    isOpen: action.payload.isOpen,
                });
                break;
            case Actions.closeModalOpened:
                trackWithLogging('close_modal_opened');
                break;
            case Actions.closeFromCloseModal:
                trackWithLogging('close_from_close_modal');
                break;
            case Actions.cancelCloseFromCloseModal:
                trackWithLogging('cancel_close_from_close_modal');
                break;
            case Actions.shareLinkLoaded:
                trackWithLogging('share_link_loaded');
                break;
        }

        return next(action);
    };
